import { useRef, useState } from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
} from "components/ui/chart/Chart";
import { useLegendToggle } from "components/ui/chart/useLegendToggle";
import EmptyState from "components/ui/empty_state/EmptyState";
import { useBuyerSupplierAwardsOverTime } from "lib/hooks/api/buyer/useBuyerSupplierAwardsOverTime";
import { SPEND_TIME_UNIT, supplierSpendFilter } from "lib/hooks/api/spend/spendFilters";
import { useSpendOverTime } from "lib/hooks/api/spend/useSpendOverTime";
import { SpendGroupBy, SupplierMentionType } from "lib/types/graphQLEnums";
import { formatShortAmount } from "lib/utils";
import { BuyerSupplierRow, toBuyerSupplierRows } from "./buyerSupplierChartRows";
import { BuyerSupplierPeriodModal, ChartPeriod, PeriodTab } from "./BuyerSupplierPeriodModal";
import { ChartSkeleton } from "./ChartSkeleton";
import { ClickToInspectTooltipContent } from "./ClickToInspectTooltipContent";
import { SpendChartError } from "./pageStates";
import { DateUnit, formatSpendValue } from "./spendChartRows";

type Props = {
  supplierGuid: string;
  buyerGuid: string;
  /** For the header of the period a click opens. */
  buyerName?: string;
  supplierName?: string;
  /** Spend is paywalled. Without it the chart shows award value alone. */
  hasSpendAccess: boolean;
  /** Which awards count, so the chart agrees with the figures and table around it. */
  supplierMentionType: SupplierMentionType;
  /** The bucket size, from the page's controls. */
  interval: DateUnit;
  /** Start of the page's date range as YYYY-MM-DD, or undefined for all time. */
  dateFrom?: string;
};

export const CHART_HEIGHT = 470;

const CONFIG: ChartConfig = {
  spend: {
    label: "Invoiced spend",
    color: "sysChart2",
    tooltipFormatter: (value: number) => formatSpendValue(value),
  },
  awardValue: {
    label: "Award value",
    color: "sysChart1",
    tooltipFormatter: (value: number) => formatSpendValue(value),
  },
};

type ChartRowsArgs = Pick<
  Props,
  "buyerGuid" | "supplierGuid" | "hasSpendAccess" | "supplierMentionType" | "interval" | "dateFrom"
> & {
  /** Over all time, the chart starts no later than this, so a recent relationship isn't one bar. */
  minFrom?: DateTime;
};

/** The chart's rows, from the pair's awards and, with spend access, its payments. */
export function useBuyerSupplierChartRows({
  buyerGuid,
  supplierGuid,
  hasSpendAccess,
  supplierMentionType,
  interval,
  dateFrom,
  minFrom,
}: ChartRowsArgs) {
  const awards = useBuyerSupplierAwardsOverTime({
    buyerGuid,
    supplierGuid,
    interval,
    publishedFrom: dateFrom,
    supplierMentionType,
  });

  const spend = useSpendOverTime(
    {
      filters: {
        ...supplierSpendFilter({ buyerGuid, supplierGuid }),
        dateFrom,
      },
      dateUnit: SPEND_TIME_UNIT[interval],
      groupBy: SpendGroupBy.None,
    },
    // The API refuses spend without the entitlement, so don't ask.
    { enabled: hasSpendAccess },
  );

  const queries = hasSpendAccess ? [awards, spend] : [awards];
  const isLoading = queries.some((query) => query.isLoading);
  const failed = queries.find((query) => query.isError);

  const spendPoints = spend.data?.points ?? [];
  const awardPoints = awards.data?.points ?? [];

  const from = chartFrom(
    dateFrom,
    [...spendPoints.map((point) => point.period), ...awardPoints.map((point) => point.period)],
    minFrom,
  );
  const rows: BuyerSupplierRow[] = isLoading
    ? []
    : toBuyerSupplierRows({
        spendPoints,
        awardPoints,
        from,
        to: DateTime.now(),
        dateUnit: interval,
      });

  return {
    rows,
    from,
    isLoading,
    isError: Boolean(failed),
    error: failed?.error,
    refetch: () => {
      for (const query of queries) {
        void query.refetch();
      }
    },
  };
}

/**
 * The page's range start, or over all time the pair's first activity, so a short relationship fills
 * the chart rather than sitting in the corner of an empty decade.
 */
function chartFrom(
  dateFrom: string | undefined,
  periods: string[],
  minFrom: DateTime | undefined,
): DateTime {
  if (dateFrom) return DateTime.fromISO(dateFrom);
  const firstActive = earliest(periods);
  if (!minFrom) return firstActive;
  return DateTime.min(firstActive, minFrom);
}

/** The earliest valid ISO date given, or now when there are none. */
function earliest(dates: (string | null | undefined)[]): DateTime {
  const valid = dates
    .filter((date): date is string => Boolean(date))
    .map((date) => DateTime.fromISO(date))
    .filter((date) => date.isValid);
  return DateTime.min(...valid) ?? DateTime.now();
}

/**
 * Invoiced spend as a line over award value as bars, both in pounds on one axis, per period across
 * the page's date range. Without spend access the line drops out and award value stands alone.
 * Clicking a period opens its awards and, with spend access, its transactions.
 */
export function BuyerSupplierChart(props: Props) {
  const { hasSpendAccess, interval, dateFrom } = props;
  const { rows, isLoading, isError, error, refetch } = useBuyerSupplierChartRows(props);
  const keys = hasSpendAccess ? ["spend", "awardValue"] : ["awardValue"];
  const { legendProps, seriesProps } = useLegendToggle(keys);
  const [opened, setOpened] = useState<{ period: ChartPeriod; tab: PeriodTab } | null>(null);
  // Set by the bar's own click handler, which runs before the chart's, so a click on a bar opens
  // its awards and a click anywhere else in the period opens its transactions.
  const clickedBar = useRef(false);

  if (isError) {
    // Either series can be the one that failed, so name the chart rather than spend.
    return <SpendChartError error={error} onRetry={refetch} subject="this chart" />;
  }

  if (isLoading) {
    return (
      <ChartFrame>
        <ChartSkeleton height={CHART_HEIGHT} label="Loading chart" />
      </ChartFrame>
    );
  }

  // Counts too, so awards with no published value still open as a period rather than "no activity".
  if (!rows.some((row) => row.spend || row.awardValue || row.awardCount || row.transactions)) {
    return (
      <ChartFrame>
        <EmptyState
          type="noResults"
          heading={dateFrom ? "No activity in this period" : "No activity yet"}
          description={emptyDescription(Boolean(dateFrom), hasSpendAccess)}
        />
      </ChartFrame>
    );
  }

  const openPeriod = (row: BuyerSupplierRow) => {
    const onBar = clickedBar.current;
    clickedBar.current = false;
    setOpened({
      period: {
        label: row.period,
        interval,
        start: row.periodStart,
        // The first period can start before the page's range; its bar only covers the part inside it.
        from: dateFrom && dateFrom > row.periodStart ? dateFrom : row.periodStart,
        to: DateTime.fromISO(row.periodStart).endOf(interval).toISODate() ?? row.periodStart,
        awardCount: row.awardCount,
        awardValue: row.awardValue,
        transactionCount: row.transactions,
      },
      tab: initialTab(row, { onBar, hasSpendAccess }),
    });
  };

  return (
    <>
      <StyledChartContainer config={CONFIG}>
        <ComposedChart
          accessibilityLayer
          data={rows}
          margin={{ left: 12, right: 12 }}
          // The whole period is the click target, not just its bar, which on a quiet period is a
          // few pixels tall.
          onClick={(state) => {
            const row = rows[state?.activeTooltipIndex ?? -1];
            if (row) {
              openPeriod(row);
            } else {
              clickedBar.current = false;
            }
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="period"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
          />
          <YAxis
            yAxisId="value"
            width={72}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: number) => formatShortAmount(value)}
            domain={[0, "auto"]}
            label={{ value: "Transaction value", angle: -90, position: "insideLeft" }}
          />
          <ChartTooltip
            // Spend first, as on the spend trend chart. The line is drawn last, so it would
            // otherwise sit under the award value.
            itemSorter={(item) => (item.dataKey === "spend" ? 0 : 1)}
            // Every period opens its awards, so the hint shows with or without spend access.
            content={<ClickToInspectTooltipContent />}
          />
          <ChartLegend verticalAlign="bottom" content={<ChartLegendContent {...legendProps} />} />
          <Bar
            yAxisId="value"
            dataKey="awardValue"
            fill="var(--color-awardValue)"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            onClick={() => {
              clickedBar.current = true;
            }}
            {...seriesProps("awardValue")}
          />
          {/* Declared after the bars so the line draws on top of them. */}
          {hasSpendAccess && (
            <Line
              yAxisId="value"
              dataKey="spend"
              type="linear"
              stroke="var(--color-spend)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
              {...seriesProps("spend")}
            />
          )}
        </ComposedChart>
      </StyledChartContainer>
      {opened && (
        <BuyerSupplierPeriodModal
          buyerGuid={props.buyerGuid}
          supplierGuid={props.supplierGuid}
          buyerName={props.buyerName}
          supplierName={props.supplierName}
          supplierMentionType={props.supplierMentionType}
          publishedFrom={dateFrom}
          hasSpendAccess={hasSpendAccess}
          period={opened.period}
          initialTab={opened.tab}
          onClose={() => setOpened(null)}
        />
      )}
    </>
  );
}

/**
 * The tab for what was clicked: a bar opens its awards, anywhere else its transactions. A period
 * with only one of the two opens on that one, and without spend access there are only awards.
 */
function initialTab(
  row: BuyerSupplierRow,
  { onBar, hasSpendAccess }: { onBar: boolean; hasSpendAccess: boolean },
): PeriodTab {
  if (!hasSpendAccess || onBar) {
    return "awards";
  }
  return row.transactions || !row.awardCount ? "transactions" : "awards";
}

/** Invoices only come into it for someone who can see spend; for anyone else the chart is awards. */
function emptyDescription(inPeriod: boolean, hasSpendAccess: boolean): string {
  const what = hasSpendAccess ? "awarded or invoiced" : "awarded";
  return inPeriod
    ? `Nothing was ${what} between this buyer and supplier in this period. Try a longer date range.`
    : `Nothing has been ${what} between this buyer and supplier.`;
}

const ChartFrame = styled.div({
  height: CHART_HEIGHT,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledChartContainer = styled(ChartContainer)({
  height: CHART_HEIGHT,
  width: "100%",
  "& .recharts-surface": {
    cursor: "pointer",
  },
});
